/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Leaderboard state management
 * leaderboard.store.ts - Zustand store for leaderboard state and configuration
 */

import { create } from "@game/state";
import type { LeaderboardConfig } from "./leaderboard.ui";

const DEFAULT_CONFIG: LeaderboardConfig = {
  containerSelector: "leaderboard",
  contentSelector: "leaderboard-content",
  animationDuration: 200,
  enableColorHighlights: true,
  enableStickyPlayer: true,
};

interface LeaderboardState {
  visible: boolean;
  config: LeaderboardConfig;
  maxPlayers: number | null;
}

interface LeaderboardActions {
  setVisible: (visible: boolean) => void;
  updateConfig: (config: Partial<LeaderboardConfig>) => void;
  setMaxPlayers: (count: number | null) => void;
  resetMaxPlayers: () => void;
}

export type LeaderboardStore = LeaderboardState & LeaderboardActions;

export const useLeaderboardStore = create<LeaderboardStore>((set) => ({
  // Initial state
  visible: true,
  config: DEFAULT_CONFIG,
  maxPlayers: null,

  // Actions
  setVisible: (visible: boolean) => set({ visible }),

  updateConfig: (newConfig: Partial<LeaderboardConfig>) =>
    set((state) => ({
      config: { ...state.config, ...newConfig },
    })),

  setMaxPlayers: (count: number | null) => set({ maxPlayers: count }),

  resetMaxPlayers: () => set({ maxPlayers: null }),
}));
